import React, { useMemo, useState, useRef, useEffect } from 'react';
import globe from 'assets/globe-network.png';
import transmissionAudio from 'assets/command-transmissio.m4a';

// If the pasted image is saved differently, replace the path above with the correct import.

const Signal = () => {
  // Responsive node density: fewer pulse nodes on smaller screens for performance / clarity
  const pulseNodes = useMemo(() => {
    let count = 28;
    try {
      const w = window.innerWidth;
      if (w < 480) count = 14; else if (w < 768) count = 20; // scale nodes down on narrow viewports
    } catch { /* SSR safeguard */ }
    return [...Array(count)].map((_, idx) => ({
      id: idx,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: (Math.random() * 4).toFixed(2)
    }));
  }, []);

  // --- Command Center Transmission ---
  const [transmitting, setTransmitting] = useState(false);
  const [paused, setPaused] = useState(false);
  const [hasHeardTransmission, setHasHeardTransmission] = useState(false);
  const [playError, setPlayError] = useState(null);
  const audioRef = useRef(null);
  // Real transmission audio asset (m4a)
  const AUDIO_SRC = transmissionAudio;

  useEffect(() => {
    try {
      const stored = localStorage.getItem('hasHeardTransmission');
      if(stored === 'true') setHasHeardTransmission(true);
    } catch { /* ignore */ }
  }, []);

  const toggleTransmission = () => {
    const el = audioRef.current;
    if(!el) return;
    // If currently playing -> pause
    if(transmitting){
      el.pause();
      setTransmitting(false);
      setPaused(true);
      return;
    }
    // If paused -> resume
    if(paused){
      setPlayError(null);
      el.play()
        .then(()=> { setTransmitting(true); setPaused(false); })
        .catch(err=>{
          setPlayError('Unable to resume transmission (browser blocked autoplay). Tap again or adjust settings.');
          // eslint-disable-next-line no-console
          console.warn('Transmission resume failed:', err);
        });
      return;
    }
    // Fresh play (or replay)
    try { el.currentTime = 0; } catch {/* ignore */}
    setPlayError(null);
    el.play()
      .then(()=> { setTransmitting(true); setPaused(false); })
      .catch((err)=>{
        setPlayError('Unable to start transmission (browser blocked autoplay). Tap again or adjust settings.');
        // eslint-disable-next-line no-console
        console.warn('Transmission play failed:', err);
      });
  };

  const stopTransmission = () => {
    const el = audioRef.current;
    if(!el) return;
    try { el.pause(); el.currentTime = 0; } catch {/* ignore */}
    setTransmitting(false);
    setPaused(false);
  };

  return (
    <div className="d-flex align-items-center justify-content-center" style={{minHeight:'calc(100vh - 120px)', background:'#0b0d17', color:'#f5f6fa', padding:'1rem'}}>
      <div className="container-fluid">
        <div className="row justify-content-center align-items-center g-4">
          {/* Left Content */}
          <div className="col-xl-5 col-lg-6 col-md-10">
            <div className="mb-4">
              <span className="badge bg-primary bg-opacity-25 text-primary mb-3" style={{backdropFilter:'blur(4px)'}}>Signal Intelligence</span>
              <h1 className="display-5 fw-bold" style={{lineHeight:1.1, fontSize:'clamp(1.5rem,4vw,2.5rem)'}}>
                Global Trust <span className="text-gradient" style={{background:'linear-gradient(90deg,#6366f1,#8b5cf6,#ec4899)', WebkitBackgroundClip:'text', color:'transparent'}}>Signal Mesh</span>
              </h1>
              <p className="lead mt-4" style={{maxWidth:'560px', color:'#ffffff'}}>
                This is the future forward approach for capital: ethical, relational, and ready.
              </p>
            </div>
            
            <div className="row g-3 mb-4">
              {[
                {label:'Inertia: K-R-O', value:'512', delta:'+18', thumbs: true},
                {label:'Acceleration: This OS', value:'94.2', delta:'+0.7%', thumbs: true},
                {label:'Latency: B2All', value:'128', delta:'+12%'},
                {label:'Anomaly: Purple Pill', value:'3', delta:'Daily %'},
                {label:'Settlement Velocity', value:'T+0', delta:'Canton DvP'},
                {label:'Marker Assignment', value:'3', delta:'Active'}
              ].map((k,i)=>(
                <div key={i} className="col-12 col-sm-6">
                  <div className="p-3 rounded-3 h-100 shadow-sm" style={{background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)'}}>
                    <div className="small text-uppercase fw-semibold mb-1" style={{letterSpacing:'.05em', color:'#ffffff'}}>{k.label}</div>
                    <div className="d-flex align-items-end">
                      <div className="h4 mb-0 fw-bold text-white me-2">{k.value}</div>
                      <span className={`badge ${k.label === 'Anomaly: Purple Pill' ? 'text-white' : k.label === 'Settlement Velocity' ? 'text-white' : k.label === 'Marker Assignment' ? 'text-white' : (k.delta.startsWith('-')?'bg-danger':'bg-success')} bg-opacity-75`} style={k.label === 'Anomaly: Purple Pill' ? {backgroundColor: '#8b5cf6'} : k.label === 'Settlement Velocity' ? {backgroundColor: '#10b981'} : k.label === 'Marker Assignment' ? {backgroundColor: '#6366f1'} : {}}>{k.delta}{k.thumbs && <><i className="bi bi-hand-thumbs-up-fill ms-1" style={{color:'#8b5cf6'}}></i><i className="bi bi-hand-thumbs-up-fill" style={{color:'#8b5cf6'}}></i></>}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="d-flex flex-wrap gap-3">
              <button 
                className="btn btn-primary btn-lg px-4 w-100 w-sm-auto btn-responsive" 
                style={{boxShadow:'0 8px 24px -8px rgba(99,102,241,.6)'}}
                onClick={() => window.open('https://www.kromarketplace.com/', '_blank')}
              >
                Launch Marketplace™ <i className="bi bi-arrow-right ms-2"></i>
              </button>
              <button className="btn btn-outline-light btn-lg px-4 w-100 w-sm-auto btn-responsive">
                View Media Streams
              </button>
              <button
                type="button"
                onClick={toggleTransmission}
                aria-pressed={transmitting}
                className={`btn btn-lg px-4 d-flex align-items-center justify-content-center gap-2 w-100 w-sm-auto btn-responsive ${transmitting ? 'btn-danger transmitting' : (paused ? 'btn-warning' : (hasHeardTransmission ? 'btn-outline-secondary' : 'btn-secondary'))}`}
                title={
                  transmitting
                    ? 'Pause Command Center Transmission'
                    : paused
                      ? 'Resume Command Center Transmission'
                      : (hasHeardTransmission ? 'Replay Command Center Transmission' : 'Play Command Center Transmission')
                }
                style={transmitting ? {position:'relative'} : {}}
              >
                <i className="bi bi-broadcast-pin" />
                {transmitting && 'Pause Transmission'}
                {!transmitting && paused && 'Resume Transmission'}
                {!transmitting && !paused && (hasHeardTransmission ? 'Replay Transmission' : 'Command Center Transmission')}
                {transmitting && <span className="transmit-dot" aria-hidden="true" />}
              </button>
              {(transmitting || paused) && (
                <button
                  type="button"
                  onClick={stopTransmission}
                  className="btn btn-outline-danger btn-lg px-4 d-flex align-items-center gap-2 w-100 w-sm-auto btn-responsive"
                  title="Stop Command Center Transmission"
                >
                  <i className="bi bi-stop-circle" /> Stop
                </button>
              )}
            </div>
            
            {playError && (
              <div className="text-warning small mt-3" role="alert" aria-live="polite">{playError}</div>
            )}
            
            <audio
              ref={audioRef}
              src={AUDIO_SRC}
              preload="auto"
              onEnded={()=> { setTransmitting(false); setPaused(false); if(!hasHeardTransmission){ setHasHeardTransmission(true); try { localStorage.setItem('hasHeardTransmission','true'); } catch {/* ignore */} } }}
              aria-hidden="true"
            />
            
            {/* Description text */}
            <div className="mt-4">
              <p className="lead text-center" style={{maxWidth:'1000px', color:'#ffffff', margin:'0 auto', lineHeight:'1.8'}}>
                Step into a new way of operating, one that amplifies clarity and purpose. Ethical. Efficient. Intentional.
                <br />
                <span className="e-pluribus-gradient" style={{
                  background:'linear-gradient(90deg,#facc15,#fbbf24,#f59e0b,#fb923c,#f472b6 80%)',
                  WebkitBackgroundClip:'text',
                  backgroundClip:'text',
                  color:'transparent',
                  fontWeight:'600',
                  letterSpacing:'.05em'
                }}>E Pluribus Unum</span>: from many signals, emerges one unified, ethical flow of exchange.
              </p>
            </div>
          </div>

          {/* Right Visual - ESG Cosmos Section */}
          <div className="col-xl-6 col-lg-7 col-md-10 position-relative">
            {/* ESG Cosmos Heading */}
            <div className="text-center mb-4 esg-cosmos-heading">
              <h4 className="fw-semibold mb-2" style={{
                fontSize:'clamp(1.1rem,1.7vw,1.6rem)',
                letterSpacing:'.08em',
                textTransform:'uppercase',
                background:'linear-gradient(90deg,#facc15,#fbbf24,#f59e0b,#fb923c,#f472b6 80%)',
                WebkitBackgroundClip:'text',
                color:'transparent',
                filter:'drop-shadow(0 0 10px rgba(250,204,21,0.45))'
              }} aria-label="Welcome to the ESG Cosmos">Welcome to the ESG Cosmos</h4>
              <div style={{height:'4px', width:'120px', margin:'4px auto 0', background:'linear-gradient(90deg,rgba(250,204,21,.85),rgba(244,114,182,.65))', borderRadius:'2px', boxShadow:'0 0 10px -2px rgba(250,204,21,.55)'}} />
            </div>
            
            {/* Globe Image Container */}
            <div className="d-flex justify-content-center align-items-center">
              <div className="globe-wrapper" style={{maxWidth:'750px', width:'100%'}}>
                <div className="w-100 d-flex justify-content-center align-items-center position-relative">
                  <div style={{position:'relative', width:'100%', maxWidth:'750px'}}>
                    <div className="globe-skeleton" aria-hidden="true" />
                    <div aria-describedby="globe-access-desc">
                      <img
                        src={globe}
                        alt="Global Signal Mesh visualization"
                        width={520}
                        height={520}
                        loading="lazy"
                        decoding="async"
                        className="img-fluid globe-img globe-rotator"
                        style={{objectFit:'contain', position:'relative', zIndex:2}}
                        onLoad={(e)=>{ const skeleton = e.currentTarget.closest('div').previousSibling; skeleton && skeleton.classList.add('loaded'); }}
                      />
                    </div>
                    <p id="globe-access-desc" className="visually-hidden">
                      Animated globe representing distributed trust signal nodes and interconnections. Rotation is decorative; data points are non-interactive.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Custom Keyframe Styles */}
      <style>{`
        /* Text Gradient */
        .text-gradient { 
          font-weight: 700; 
        }
        
        /* Pulse Animation */
        @keyframes pulse {
          0%, 100% { transform: scale(.4); opacity: .4; }
          50% { transform: scale(1); opacity: 1; }
        }
        
        /* Responsive Button Styles */
        .btn-responsive { 
          font-size: clamp(.9rem, 2.6vw, 1.125rem); 
        }
        @media (max-width: 575.98px) {
          .btn-responsive { 
            width: 100%; 
          }
          .btn-responsive i { 
            margin-left: .25rem; 
          }
        }
        @media (min-width: 576px) {
          .btn-responsive { 
            width: auto; 
          }
        }
        
        /* Globe Skeleton Loader */
        .globe-skeleton { 
          position: absolute; 
          inset: 0; 
          background: linear-gradient(135deg, rgba(99,102,241,.15), rgba(139,92,246,.08)); 
          border-radius: 50%; 
          animation: glow 2s ease-in-out infinite; 
        }
        .globe-skeleton.loaded { 
          opacity: 0; 
          transition: opacity .4s ease .15s; 
        }
        @keyframes glow { 
          0%, 100% { filter: brightness(1); opacity: .9; }
          50% { filter: brightness(1.3); opacity: .5; }
        }
        
        /* Globe Wrapper */
        .globe-wrapper { 
          margin: 0 auto; 
        }
        
        /* Globe Float Animation */
        .globe-img { 
          transform: translateY(0px); 
          animation: globeFloat 6s ease-in-out infinite;
        }
        @keyframes globeFloat {
          0%, 100% { transform: translateY(-2px); }
          50% { transform: translateY(-8px); }
        }
        @media (min-width: 1200px) { 
          .globe-img { 
            transform: translateY(-5px); 
          }
          @keyframes globeFloat {
            0%, 100% { transform: translateY(-3px); }
            50% { transform: translateY(-10px); }
          }
        }
        @media (max-width: 576px) { 
          .globe-img { 
            transform: translateY(0px); 
          }
          @keyframes globeFloat {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-6px); }
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .globe-img { 
            animation: none; 
            transform: translateY(-5px); 
          }
        }
        
        /* ESG Cosmos Heading Animations */
        .esg-cosmos-heading { 
          animation: cosmosFade .9s ease-out both; 
        }
        @keyframes cosmosFade { 
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 576px) { 
          .esg-cosmos-heading h4 { 
            font-size: 1.25rem !important; 
            letter-spacing: .08em; 
          }
        }
        @media (prefers-reduced-motion: reduce) { 
          .esg-cosmos-heading { 
            animation: none; 
          }
        }
        
        /* Animated Gradient Shimmer */
        .esg-cosmos-heading h4 {
          background-size: 300% 100%;
          animation: cosmosGradient 14s linear infinite;
          position: relative;
        }
        @keyframes cosmosGradient { 
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .esg-cosmos-heading h4::after {
          content: "";
          position: absolute;
          top: 0; 
          left: -15%;
          width: 35%; 
          height: 100%;
          background: linear-gradient(75deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.18) 45%, rgba(255,255,255,0) 90%);
          transform: skewX(-15deg);
          animation: cosmosShine 6.5s ease-in-out infinite;
          pointer-events: none;
          mix-blend-mode: screen;
        }
        @keyframes cosmosShine { 
          0%, 8% { transform: translateX(0) skewX(-15deg); opacity: 0; }
          12% { opacity: 1; }
          28% { transform: translateX(250%) skewX(-15deg); opacity: 0; }
          100% { opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) { 
          .esg-cosmos-heading h4 { 
            animation: none; 
          }
          .esg-cosmos-heading h4::after { 
            animation: none; 
          }
        }
        
        /* Transmission Button Animations */
        @keyframes pulseRing {
          0% { box-shadow: 0 0 0 0 rgba(239,68,68,.6); }
          70% { box-shadow: 0 0 0 14px rgba(239,68,68,0); }
          100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
        }
        .transmitting { 
          animation: pulseRing 1.6s ease-out infinite; 
        }
        .transmit-dot { 
          width: 10px; 
          height: 10px; 
          background: #fff; 
          border-radius: 50%; 
          box-shadow: 0 0 8px 2px rgba(255,255,255,.6); 
          display: inline-block; 
        }
        
        /* Globe Rotator Animation */
        .globe-rotator { 
          animation: spinY 60s linear infinite; 
          transform-style: preserve-3d; 
          will-change: transform; 
        }
        @keyframes spinY { 
          from { transform: rotateY(0deg); }
          to { transform: rotateY(360deg); }
        }
        @media (prefers-reduced-motion: reduce) { 
          .globe-rotator { 
            animation: none; 
          }
        }
      `}</style>
    </div>
  );
};

export default Signal;
